import { 
  LOADING_PERSONALIZATION, 
  LOADED_PERSONALIZATION, 
  PERSONALIZATION_ERROR 
} from './personalization.types';
import { decryptData } from '../../../utils/decryption';

const CACHE_KEY = 'personalization_data';
const LOADING_KEY = 'personalization_loading';

// Check if currently loading (uses sessionStorage to prevent race conditions)
const isCurrentlyLoading = () => {
  if (typeof window === 'undefined') return false;
  try {
    const loadingState = sessionStorage.getItem(LOADING_KEY);
    if (!loadingState) return false;
    const timestamp = parseInt(loadingState, 10);
    const now = Date.now();
    // If loading started more than 15 seconds ago, consider it stale
    if (now - timestamp > 15000) {
      sessionStorage.removeItem(LOADING_KEY);
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
};

const setLoading = (isLoading) => {
  if (typeof window === 'undefined') return;
  try {
    if (isLoading) {
      sessionStorage.setItem(LOADING_KEY, Date.now().toString());
    } else {
      sessionStorage.removeItem(LOADING_KEY);
    }
  } catch (e) {
    // Ignore storage errors
  }
};

/**
 * Clear personalization cache from sessionStorage
 * Called on logout or when user session expires
 */
export const clearPersonalizationCache = () => {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(CACHE_KEY);
      sessionStorage.removeItem(LOADING_KEY);
    } catch (e) {
      console.warn('[Personalization] Cache clear failed:', e);
    }
  }
};

/**
 * Fetches personalization attributes on page load for Contentful content filtering
 * 
 * FLOW:
 * 1. Check sessionStorage for cached encrypted data
 *    - If cached: Decrypt and use it (NO API call)
 *    - If no cache: Make API call once per page load
 * 
 * 2. API call happens only once (isLoading flag prevents duplicates)
 * 
 * 3. Store encrypted data in sessionStorage (visible as encrypted in network tab)
 * 
 * 4. Decrypt only when needed (from cache or API response)
 * 
 * 5. Handle logged out users gracefully (no error, return null values)
 */
export const loadPersonalizationAttributes = () => async (dispatch) => {
  // Prevent duplicate API calls
  if (isCurrentlyLoading()) {
    return;
  }

  // Check sessionStorage cache first
  if (typeof window !== 'undefined') {
    let cachedEncryptedData = null;
    try {
      cachedEncryptedData = sessionStorage.getItem(CACHE_KEY);
    } catch (e) {
      console.warn('[Personalization] sessionStorage access failed:', e);
    }
    
      if (cachedEncryptedData) {
      try {
        const DECRYPTION_KEY = process.env.GROWTHBOOK_API_RESPONSE_DECRYPTION_KEY;
        if (!DECRYPTION_KEY) {
          throw new Error('GROWTHBOOK_API_RESPONSE_DECRYPTION_KEY not configured');
        }
        
        // Decrypt cached data
        const decryptedData = await decryptData(cachedEncryptedData);
        
        // If cached data has null account_type, clear cache and proceed to API call
        // This handles the case where cache might have stale logged-out state
        if (!decryptedData.account_type || decryptedData.account_type === null) {
          try {
            sessionStorage.removeItem(CACHE_KEY);
          } catch (storageError) {
            // Ignore storage errors
          }
          // Don't return here - continue to API call
        } else {
          // Valid cached data - use it
          const payload = {
            account_type: decryptedData.account_type,
            product_type: decryptedData.product_type || [],
            country: decryptedData.geo_location || decryptedData.country || null,
            public_user_id: decryptedData.public_user_id || null, // Use public_user_id directly (already secure)
          };
          
          dispatch({
            type: LOADED_PERSONALIZATION,
            payload,
          });
          
          return;
        }
      } catch (e) {
        console.error('[Personalization] Cache error:', e.message);
        // Clear invalid cache
        try {
          sessionStorage.removeItem(CACHE_KEY);
        } catch (storageError) {
          // Ignore storage errors
        }
      }
    }
  }

  setLoading(true);
  dispatch({ type: LOADING_PERSONALIZATION });

  try {
    const API_URL = process.env.ACCOUNT_INFO_API_URL;
    const DECRYPTION_KEY = process.env.GROWTHBOOK_API_RESPONSE_DECRYPTION_KEY;

    if (!API_URL) {
      throw new Error('ACCOUNT_INFO_API_URL not configured');
    }

    if (!DECRYPTION_KEY) {
      throw new Error('GROWTHBOOK_API_RESPONSE_DECRYPTION_KEY not configured');
    }

    // Add timeout (10 seconds)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    let response;
    try {
      response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Sends HttpOnly cookies automatically
        body: JSON.stringify({}), // Empty body - API gets cookie/IP from headers
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      // Handle network errors (timeout, network down, etc.)
      if (fetchError.name === 'AbortError') {
        console.error('[Personalization] Request timeout');
        throw new Error('Request timeout');
      }
      console.error('[Personalization] Network error:', fetchError.message);
      
      // If we have cached data, use it instead of failing completely
      if (typeof window !== 'undefined') {
        const cachedData = sessionStorage.getItem(CACHE_KEY);
        if (cachedData) {
          try {
            const decryptedData = await decryptData(cachedData);
            
            // Validate cached data - don't use if account_type is null
            if (!decryptedData.account_type || decryptedData.account_type === null) {
              sessionStorage.removeItem(CACHE_KEY);
              // Don't return - let it throw and be handled in catch block
            } else {
              dispatch({
                type: LOADED_PERSONALIZATION,
                payload: {
                  account_type: decryptedData.account_type,
                  product_type: decryptedData.product_type || [],
                  country: decryptedData.geo_location || decryptedData.country || null,
                  public_user_id: decryptedData.public_user_id || null,
                },
              });
              dispatch({
                type: PERSONALIZATION_ERROR,
                payload: `Network error - using cached data`,
              });
              return;
            }
          } catch (decryptError) {
            console.warn('[Personalization] Cached data fallback failed');
          }
        }
      }
      
      throw new Error(`Network error: ${fetchError.message}`);
    }

    // Handle non-OK responses gracefully (logged out users, etc.)
    if (!response.ok) {
      // For logged out users, API may return 401/403/409 - handle gracefully
      if (response.status === 401 || response.status === 403 || response.status === 409) {
        clearPersonalizationCache();
        dispatch({
          type: LOADED_PERSONALIZATION,
          payload: {
            account_type: null,
            product_type: [],
            country: null,
            public_user_id: null,
          },
        });
        return;
      }
      
      // Handle 500 errors (server errors)
      if (response.status >= 500) {
        console.error('[Personalization] Server error:', response.status);
        // If we have cached data, use it as fallback
        if (typeof window !== 'undefined') {
          const cachedData = sessionStorage.getItem(CACHE_KEY);
          if (cachedData) {
            try {
              const decryptedData = await decryptData(cachedData);
              
              // Validate cached data - don't use if account_type is null
              if (!decryptedData.account_type || decryptedData.account_type === null) {
                sessionStorage.removeItem(CACHE_KEY);
                // Don't return - let it throw and be handled
              } else {
                dispatch({
                  type: LOADED_PERSONALIZATION,
                  payload: {
                    account_type: decryptedData.account_type,
                    product_type: decryptedData.product_type || [],
                    country: decryptedData.geo_location || decryptedData.country || null,
                    public_user_id: decryptedData.public_user_id || null,
                  },
                });
                dispatch({
                  type: PERSONALIZATION_ERROR,
                  payload: `Server error - using cached data`,
                });
                return;
              }
            } catch (decryptError) {
              console.warn('[Personalization] Cached data fallback failed');
            }
          }
        }
        
        throw new Error(`Server error (${response.status})`);
      }
      
      // For other errors (4xx except 401/403/409)
      throw new Error(`HTTP error: ${response.status}`);
    }

    const data = await response.json();

    // Check for encrypted_response field (API can return either 'encrypted_response' or 'response')
    const encryptedData = data.encrypted_response || data.response;
    
    if (!encryptedData) {
      // Handle plain JSON response (for local dev/testing)
      if (data.account_type !== undefined) {
        // Check if user is logged out in plain JSON response
        if (!data.account_type || data.account_type === null) {
          clearPersonalizationCache();
          dispatch({
            type: LOADED_PERSONALIZATION,
            payload: {
              account_type: null,
              product_type: [],
              country: null,
              public_user_id: null,
            },
          });
          return;
        }
        
        const payload = {
          account_type: data.account_type,
          product_type: data.product_type || [],
          country: data.geo_location || data.country || null,
          public_user_id: data.public_user_id || null, // Use public_user_id directly
        };
        
        dispatch({
          type: LOADED_PERSONALIZATION,
          payload,
        });
        return;
      }
      
      // If no encrypted data and no account_type, user is logged out
      clearPersonalizationCache();
      dispatch({
        type: LOADED_PERSONALIZATION,
        payload: {
          account_type: null,
          product_type: [],
          country: null,
          public_user_id: null,
        },
      });
      return;
    }

    // Decrypt the encrypted response
    let decryptedData;
    try {
      decryptedData = await decryptData(encryptedData);
    } catch (decryptError) {
      console.error('[Personalization] Decryption failed:', decryptError.message);
      clearPersonalizationCache();
      throw new Error(`Decryption failed: ${decryptError.message}`);
    }

    // Check if user is logged out (account_type is null in response)
    if (!decryptedData.account_type || decryptedData.account_type === null) {
      clearPersonalizationCache();
      dispatch({
        type: LOADED_PERSONALIZATION,
        payload: {
          account_type: null,
          product_type: [],
          country: null,
          public_user_id: null,
        },
      });
      return; // Exit early - don't cache null data
    }

    // Store encrypted data in sessionStorage (only for logged-in users)
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem(CACHE_KEY, encryptedData);
      } catch (e) {
        console.warn('[Personalization] Failed to cache data:', e);
      }
    }

    // Map country field (handle geo_location or country)
    const payload = {
      account_type: decryptedData.account_type,
      product_type: decryptedData.product_type || [],
      country: decryptedData.geo_location || decryptedData.country || null,
      // Backend generates public_user_id via get_public_id_from_user_id():
      // - Converts user_id → Customer → serialized customer ID (pid_eci:XXXXXX)  
      // - Safe for GrowthBook bucketing, no frontend hashing needed
      public_user_id: decryptedData.public_user_id || null,
    };

    dispatch({
      type: LOADED_PERSONALIZATION,
      payload,
    });
  } catch (error) {
    console.error('[Personalization] Error:', error.message);
    
    dispatch({
      type: PERSONALIZATION_ERROR,
      payload: error.message,
    });

    // Dispatch null values on error (graceful degradation)
    dispatch({
      type: LOADED_PERSONALIZATION,
      payload: {
        account_type: null,
        product_type: [],
        country: null,
        public_user_id: null,
      },
    });
  } finally {
    setLoading(false);
  }
};
